import { Fragment, useRef, useLayoutEffect, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { CheckIcon, ExclamationCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

/*
open/setOpen  (both required): boolean to control the modal opening and closing
    - Can also be passed in as a function, but requires the parent component to set the state to false instead of the modal
status (optional): 
    - success: green check icon
    - error: red exclamation icon
    - question: blue question mark icon
title (optional): title of the modal as large text at the top
body (optional): body of the modal as smaller text below the title in a lighter gray color
confirmText (optional): text for the confirm button (default is 'Confirm')
cancelText (optional): text for the cancel button (default is 'Cancel')
onConfirm (required): callback function to run when the confirm button is clicked

Notes:
- You don't need to have this component set as {open && <Modal ... />}, the component will handle the opening and closing itself
- Refer to the LargeActionModal.js file if you want to see a staggered body text example with line breaks
*/

export default function Modal({
    open,
    setOpen,
    status,
    title,
    body,
    side = null,
    sideFooter = null,
    confirmText,
    cancelText,
    onConfirm,
    onCancel,
    dontCloseOnConfirm = false,
    confirmButtonStyling = null,
    cancelButtonStyling = null,
    focusConfirmOnLoad = false,
    extraButtonText = null,
    extraButtonFunction,
    extraButtonCustomStyle = '',
}) {
    const cancelButtonRef = useRef(null);
    const confirmButtonRef = useRef(null);
    const mainRef = useRef(null);
    const roRef = useRef(null);
    const [mainHeight, setMainHeight] = useState(0);

    const measureNow = () => {
        requestAnimationFrame(() => {
            setMainHeight(mainRef.current?.offsetHeight || 1);
        });
    };

    useLayoutEffect(() => {
        if (!open || !mainRef.current) return;
        measureNow();
        roRef.current = new ResizeObserver(measureNow);
        roRef.current.observe(mainRef.current);
        return () => roRef.current?.disconnect();
    }, [open]);

    useEffect(() => {
        if (!open) setMainHeight(0);
    }, [open]);

    const viewportCap = Math.floor(window.innerHeight * 0.8);
    const sideMax = Math.min(mainHeight || 1, viewportCap);

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                className='relative z-10'
                initialFocus={focusConfirmOnLoad ? confirmButtonRef : cancelButtonRef}
                onClose={setOpen}
            >
                <Transition.Child
                    as={Fragment}
                    enter='ease-out duration-300'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='ease-in duration-200'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                    afterEnter={measureNow}
                >
                    <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
                </Transition.Child>

                <div className='overflow-y-auto fixed inset-0 z-10 w-screen'>
                    <div className='flex justify-center items-end p-4 min-h-full text-center sm:items-center sm:p-0'>
                        <Transition.Child
                            as={Fragment}
                            enter='ease-out duration-300'
                            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                            enterTo='opacity-100 translate-y-0 sm:scale-100'
                            leave='ease-in duration-200'
                            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
                            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                        >
                            <Dialog.Panel
                                className={`relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full ${
                                    side ? 'sm:max-w-3xl' : 'sm:max-w-lg'
                                } sm:p-6 max-h-[80vh]`}
                            >
                                <div className='items-start sm:flex sm:gap-4'>
                                    <div ref={mainRef} className='flex-1 min-w-0'>
                                        {status === 'success' && (
                                            <div className='flex justify-center items-center mx-auto w-12 h-12 bg-green-100 rounded-full'>
                                                <CheckIcon className='w-6 h-6 text-green-600' aria-hidden='true' />
                                            </div>
                                        )}
                                        {status === 'error' && (
                                            <div className='flex justify-center items-center mx-auto w-12 h-12 bg-red-100 rounded-full'>
                                                <ExclamationCircleIcon className='w-6 h-6 text-red-600' aria-hidden='true' />
                                            </div>
                                        )}
                                        {status === 'question' && (
                                            <div className='flex justify-center items-center mx-auto w-12 h-12 bg-blue-100 rounded-full'>
                                                <QuestionMarkCircleIcon
                                                    className='w-6 h-6 text-blue-600'
                                                    aria-hidden='true'
                                                />
                                            </div>
                                        )}

                                        <div className='mt-3 text-center sm:mt-5'>
                                            {title && (
                                                <Dialog.Title className='text-lg font-semibold leading-6 text-gray-900'>
                                                    {title}
                                                </Dialog.Title>
                                            )}
                                            {body && (
                                                <div className='flex justify-center mt-2'>
                                                    <div className='text-gray-500 text-md'>{body}</div>
                                                </div>
                                            )}
                                        </div>

                                        <div className='flex flex-col gap-3 mt-5 sm:mt-6 sm:flex-row'>
                                            <button
                                                ref={cancelButtonRef}
                                                type='button'
                                                className={`${
                                                    cancelButtonStyling ||
                                                    'inline-flex justify-center px-3 py-2 w-full text-sm font-semibold text-gray-900 bg-white rounded-md ring-1 ring-inset ring-gray-300 shadow-sm hover:bg-gray-50'
                                                } sm:flex-1 sm:order-1`}
                                                onClick={() => {
                                                    setOpen(false);
                                                    if (typeof onCancel === 'function') onCancel();
                                                }}
                                            >
                                                {cancelText || 'Cancel'}
                                            </button>

                                            {extraButtonText && (
                                                <button
                                                    type='button'
                                                    className={`${
                                                        extraButtonCustomStyle ||
                                                        'inline-flex justify-center px-3 py-2 w-full text-sm font-semibold text-gray-900 bg-white rounded-md ring-1 ring-inset ring-gray-300 shadow-sm hover:bg-gray-50'
                                                    } sm:flex-1 sm:order-2`}
                                                    onClick={() => {
                                                        if (!dontCloseOnConfirm) setOpen(false);
                                                        extraButtonFunction();
                                                    }}
                                                >
                                                    {extraButtonText}
                                                </button>
                                            )}

                                            <button
                                                ref={confirmButtonRef}
                                                type='button'
                                                className={`${
                                                    confirmButtonStyling ||
                                                    'inline-flex justify-center px-3 py-2 w-full text-sm font-semibold text-white bg-indigo-600 rounded-md shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                                                } sm:flex-1 sm:order-3`}
                                                onClick={() => {
                                                    if (!dontCloseOnConfirm) setOpen(false);
                                                    onConfirm();
                                                }}
                                            >
                                                {confirmText || 'Confirm'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* SIDE (scrolls if taller than main) */}
                                    {side && (
                                        <aside className='flex flex-col mt-6 sm:mt-0 sm:border-l sm:pl-3'>
                                            <div
                                                className='overflow-y-auto flex-1 pr-2 transition-opacity'
                                                style={{
                                                    maxHeight: sideMax,
                                                    minHeight: mainHeight || undefined,
                                                    opacity: mainHeight ? 1 : 0.999,
                                                }}
                                            >
                                                {side}
                                            </div>
                                            {sideFooter && <div>{sideFooter}</div>}
                                        </aside>
                                    )}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
